import {
  Autocomplete,
  Card,
  CardContent,
  debounce,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { ConversationStatus } from '@provision/conversation-types';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { identity, isNullish } from 'remeda';
import { DynamicIcon, Tooltip } from 'src/components';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { Conversation } from 'src/graphql';
import { getRequesterDisplayName } from 'src/graphql/entities/conversation/conversation.mappings.utils';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { formatDisplayDate } from 'src/utils/date';
import { useScrollPaginateHandler } from 'src/utils/hooks';

interface ConversationSelectorProps {
  conversation?: Conversation | null;
  filters?: Conversation[];
  deletable?: boolean;
  title?: string;
  partnerId?: number;
  setConversation?: (conversation: Conversation | null) => void;
}

export interface ConversationOption {
  id: string | null;
  label: string;
  friendlyId?: string;
}

const SEARCH_TARGET_ATTRIBUTE_IDS = [
  'conversation/conversationId',
  'client/conversationParticipant/requesterForConversations/conversationId',
  'clientPhoneNumber/client/addresses/associatedConversations/conversationId',
  'clientEmail/client/addresses/associatedConversations/conversationId',
  'address/associatedConversations/conversationId',
  'client/conversationParticipant/conversationChannels/participations/conversationId',
];

export const ConversationSelector = ({
  conversation,
  deletable,
  title,
  filters,
  partnerId,
  setConversation,
}: ConversationSelectorProps) => {
  const [value, setValue] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const theme = useTheme();

  const conversationFilters = useMemo(() => {
    const currentFilters: ConditionInput[] = [
      {
        attribute: 'conversationStatusId',
        operator: '!=',
        value: ConversationStatus.Closed,
      },
    ];

    if (typeof conversation?.partnerId === 'number') {
      currentFilters.push({
        attribute: 'partnerId',
        operator: '=',
        value: conversation.partnerId.toString(),
      });
    }

    if (!isNullish(partnerId)) {
      currentFilters.push({
        attribute: 'partnerId',
        operator: '=',
        value: partnerId.toString(),
      });
    }

    if (filters?.length) {
      currentFilters.push({
        attribute: 'conversationId',
        operator: 'not_in',
        value: filters.map((c) => c.id).join(','),
      });
    }

    return currentFilters;
  }, [conversation?.partnerId, filters, partnerId]);

  const {
    records: conversations,
    loadMore,
    isLoading,
    hasMore,
  } = useSearch({
    entityName: EntityNames.Conversation,
    searchTerm,
    filter: conversationFilters,
    targetIdAttributes: SEARCH_TARGET_ATTRIBUTE_IDS,
  }) ?? {};

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: 200,
  });

  const filteredConversations = useMemo(
    () =>
      conversations?.map((conversation) => ({
        label: conversation.subject,
        id: conversation.id,
        friendlyId: conversation.friendlyId,
      })) || [],
    [conversations],
  );

  const handleOnDelete = useCallback(() => {
    setConversation?.(null);
  }, [setConversation]);

  const onInputChange = debounce(
    ({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchTerm(target.value);
    },
    SEARCH_DEBOUNCE_DELAY,
  );

  return conversation ? (
    <Card>
      <CardContent>
        <Typography
          gutterBottom
          variant="overline"
          borderBottom="1px solid"
          borderColor={theme.palette.custom.OV.sys.outlineVariant}
          color={theme.palette.custom.OV.sys.outline}
          display="block"
        >
          {title}
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="start">
          <div>
            <Typography variant="caption" color={theme.palette.custom.OV.sys.onSurface}>
              {conversation?.friendlyId} | {conversation?.subject}
            </Typography>
            <Typography
              variant="caption"
              color={theme.palette.custom.OV.sys.onSurfaceVariant}
              display="flex"
            >
              {getRequesterDisplayName(conversation.requesterFullName)
                ? `Requester: ${getRequesterDisplayName(conversation.requesterFullName)} | `
                : ''}
              Created: {formatDisplayDate(conversation?.createdAt)}
            </Typography>
          </div>
          {deletable ? (
            <Tooltip title="Delete">
              <IconButton color="error" onClick={handleOnDelete}>
                <DynamicIcon name="delete_forever" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  ) : (
    <Autocomplete
      autoHighlight
      value={value}
      loading={isLoading}
      fullWidth
      getOptionLabel={(option) =>
        option.friendlyId ? `${option.friendlyId} | ${option.label}` : option.label
      }
      onChange={(_, conversationToBeSelected) => {
        if (conversationToBeSelected) {
          const currentConversation =
            conversations?.find(
              (conversation) => conversation.id === conversationToBeSelected.id,
            ) ?? null;
          setConversation?.(currentConversation);
          setValue(null);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search & Select Conversation"
          autoFocus
          onChange={onInputChange}
          InputProps={{
            ...params.InputProps,
            style: {
              padding: '0.375rem 1rem',
              borderRadius: '1rem',
            },
            startAdornment: (
              <InputAdornment position="start">
                <DynamicIcon name="search" />
              </InputAdornment>
            ),
            endAdornment: null,
          }}
        />
      )}
      options={filteredConversations}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id}>
          <ListItemText
            primary={
              option.friendlyId ? `${option.friendlyId} | ${option.label}` : option.label
            }
            primaryTypographyProps={{ variant: 'body2', color: 'text.primary', noWrap: true }}
          />
        </ListItem>
      )}
      ListboxProps={{
        onScroll: handleScroll,
      }}
      filterOptions={identity}
    />
  );
};
